import React, { useState } from "react";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import FieldDropdown from "../common/InputField/FieldDropdown";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS, ACTION_POPUP_CONTENT_INNER_CLASS } from "../popup/popupLayoutSpec";
import { runTextPipeline } from "../../utils/contentSafety";
import {
  AccountPopupStepFooter,
  ACCOUNT_POPUP_MUTED_PANEL,
} from "./accountPopupFields";
import {
  AdminPanelTitle,
  AdminSupportingText,
  AdminPageTitle,
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminMainText,
  AdminHeaderAction,
  adminWizardDropdownClasses,
  adminWizardTextareaClasses,
} from "../../sections/AdminDashboard/shared/adminUx";

const AccountClosureWidget = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedReason, setSelectedReason] = useState(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);

  const reasonOptions = [
    { label: "Business needs have changed.", value: "Business needs have changed." },
    { label: "Found services through another platform.", value: "Found services through another platform." },
    { label: "Expectations not met by Trades Poster.", value: "Expectations not met by Trades Poster." },
    { label: "Experienced difficulties with platform usability.", value: "Experienced difficulties with platform usability." },
    { label: "Concerns about business data privacy.", value: "Concerns about business data privacy." },
    { label: "Others", value: "Others" },
  ];

  const steps = [
    { title: "Reason for Deactivation", field: "reason", type: "dropdown" },
    { title: "Additional Feedback", field: "feedback", type: "textarea" },
    { title: "Confirmation", field: "confirmation", type: "checkbox" },
  ];

  const handleNext = () => {
    if (currentStep === 1 && additionalFeedback.trim()) {
      const result = runTextPipeline(additionalFeedback.trim(), "support");
      if (!result.valid) {
        setFeedbackError(result.message || "Please use clear, appropriate text.");
        return;
      }
    }
    if (currentStep === 2 && !isConfirmed) return;

    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      setIsPopupOpen(false);
      setCurrentStep(0);
      setSelectedReason(null);
      setAdditionalFeedback("");
      setIsConfirmed(false);
    }
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[460px] h-[220px] account-widget !bg-red-50">
        <div className="flex items-center justify-between h-[49px] py-3 px-4 border-b border-red-200">
          <AdminPanelTitle className="text-offblack">Delete Account</AdminPanelTitle>
        </div>

        <div className="p-4">
          <AdminSupportingText className="text-alertred block mb-5">
            Remember, this information is necessary to ensure the highest quality of service on
            Trades Poster. It enables customers to trust the professionals they hire through our
            platform. Thank you for your cooperation and understanding.
          </AdminSupportingText>
          <AdminHeaderAction
            type="button"
            className="!bg-alertred hover:opacity-90"
            onClick={() => setIsPopupOpen(true)}
          >
            Proceed
          </AdminHeaderAction>
        </div>
      </DashboardMainContainer>

      {isPopupOpen && (
        <Popup
          setTrigger={() => {}}
          ShowCrossButton={true}
          cross={true}
          onClose={() => setIsPopupOpen(false)}
          businessDashboard={true}
          hideLogoInHeader={true}
        >
          <div
            className="overflow-auto relative flex items-center justify-center"
            style={{ minHeight: "calc(100vh - 120px)" }}
          >
            <div className={POPUP_FORM_CONTENT_CLASS}>
              <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
                <form onSubmit={(e) => e.preventDefault()}>
                  {currentStepData.type === "dropdown" ? (
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>{currentStepData.title}</AdminDetailFieldLabel>
                      <FieldDropdown
                        surface="muted"
                        fullWidth
                        value={selectedReason ?? ""}
                        onChange={(e) => setSelectedReason(e.value)}
                        options={reasonOptions}
                        placeholder="Select the primary reason for deactivation"
                        className={adminWizardDropdownClasses(false, true)}
                        panelClassName={ACCOUNT_POPUP_MUTED_PANEL}
                        showClear={false}
                        appendTo={null}
                      />
                    </div>
                  ) : null}

                  {currentStepData.type === "textarea" ? (
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel htmlFor="deactivation-feedback">
                        {currentStepData.title}
                      </AdminDetailFieldLabel>
                      <textarea
                        id="deactivation-feedback"
                        placeholder="Your feedback is crucial to us. If you have any additional thoughts about your experience, challenges faced, ideas for platform improvement, or positive aspects you'd like to mention, we welcome your input."
                        value={additionalFeedback}
                        onChange={(e) => {
                          setAdditionalFeedback(e.target.value);
                          setFeedbackError("");
                        }}
                        rows={4}
                        className={adminWizardTextareaClasses(Boolean(feedbackError))}
                        style={{ resize: "none" }}
                      />
                      {feedbackError ? (
                        <AdminFormFieldError>{feedbackError}</AdminFormFieldError>
                      ) : null}
                    </div>
                  ) : null}

                  {currentStepData.type === "checkbox" ? (
                    <div>
                      <AdminPageTitle className="text-center pb-0 text-offblack mb-6">
                        {currentStepData.title}
                      </AdminPageTitle>
                      <div className="flex items-start gap-3">
                        <label
                          className="relative flex items-center rounded-full cursor-pointer flex-shrink-0 mt-0.5"
                          htmlFor="confirmation"
                        >
                          <input
                            type="checkbox"
                            className="peer relative h-4 w-4 cursor-pointer appearance-none rounded border border-mediumgray checked:border-primaryblue checked:bg-primaryblue"
                            id="confirmation"
                            checked={isConfirmed}
                            onChange={(e) => setIsConfirmed(e.target.checked)}
                          />
                          <span className="absolute text-white opacity-0 pointer-events-none top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 peer-checked:opacity-100">
                            <i className="fi fi-rs-check text-[10px]" />
                          </span>
                        </label>
                        <div>
                          <AdminMainText className="text-offblack block">
                            Account deactivation confirmation
                          </AdminMainText>
                          <AdminSupportingText className="text-mediumgray block mt-2">
                            Confirm your request to deactivate your business account. This will
                            cease all current engagements with service providers on Trades Poster,
                            and all saved preferences or business listings will be permanently
                            removed.
                          </AdminSupportingText>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <AccountPopupStepFooter
                    showBack={currentStep > 0}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={handleNext}
                    nextLabel={isLastStep ? "Delete" : "Next"}
                    nextClassName={isLastStep ? "!bg-alertred hover:opacity-90" : ""}
                    nextDisabled={isLastStep && !isConfirmed}
                  />
                </form>
              </div>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};

export default AccountClosureWidget;
